// src/features/owner/OwnerPage.tsx — route entry: gate → resolve section → OwnerLayout.
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { getOwnerSection } from '@/core/data/owner-sections';
import { Button } from '@/ui';
import { Icon } from '@/icons';
import { ownerService } from '@/core/services/ownerService';
import { OwnerGuard } from './OwnerGuard';
import { OwnerLayout } from './OwnerLayout';
import { PlaceholderSection } from './sections/PlaceholderSection';
import { DashboardSection } from './sections/DashboardSection';
import { UsersSection } from './sections/users/UsersSection';
import { VerificationsSection } from './sections/users/VerificationsSection';
import { UserRolesSection } from './sections/users/UserRolesSection';
import { SuspendedUsersSection } from './sections/users/SuspendedUsersSection';
import { BannedUsersSection } from './sections/users/BannedUsersSection';
import { PostsSection } from './sections/content/PostsSection';
import { ReportsSection } from './sections/content/ReportsSection';
import { ContentModerationSection } from './sections/content/ContentModerationSection';
import { HashtagsSection } from './sections/content/HashtagsSection';
import { MediaLibrarySection } from './sections/content/MediaLibrarySection';
import { FeedbackSection } from './sections/content/FeedbackSection';
import { CouncilTeamsSection } from './sections/content/CouncilTeamsSection';
import { OverviewSection } from './sections/analytics/OverviewSection';
import { UserStatisticsSection } from './sections/analytics/UserStatisticsSection';
import { EngagementSection } from './sections/analytics/EngagementSection';
import { GrowthSection } from './sections/analytics/GrowthSection';
import { RetentionSection } from './sections/analytics/RetentionSection';
import { SystemHealthSection } from './sections/system/SystemHealthSection';
import { AuditLogsSection } from './sections/system/AuditLogsSection';
import { OwnerSettingsSection } from './sections/system/OwnerSettingsSection';
import { ApiIntegrationsSection } from './sections/system/ApiIntegrationsSection';
import { BackupsSection } from './sections/system/BackupsSection';
import { TopicRefinerSection } from './sections/system/TopicRefinerSection';
import { LinkPreviewsSection } from './sections/system/LinkPreviewsSection';
import { AppealsSection } from './sections/appeals/AppealsSection';
import { DateRangePicker } from './components/DateRangePicker';

async function exportReport(rangeDays: number) {
  const data = await ownerService.getDashboard(rangeDays);
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'owner-dashboard.json';
  a.click();
  URL.revokeObjectURL(url);
}

export function OwnerPage() {
  const { section } = useParams();
  const resolved = section ? getOwnerSection(section) : undefined;
  // Unknown section → behave as Dashboard (defensive; sidebar never links here).
  const activeId = resolved?.id ?? 'dashboard';
  const title = resolved?.label ?? 'Dashboard';

  const [rangeDays, setRangeDays] = useState(7);

  const dashboardActions = activeId === 'dashboard' ? (
    <>
      <DateRangePicker valueDays={rangeDays} onChange={setRangeDays} />
      <Button variant="primary" onClick={() => exportReport(rangeDays)}>
        <Icon name="download" size={14} /> Export Report
      </Button>
    </>
  ) : undefined;

  return (
    <OwnerGuard>
      <OwnerLayout activeId={activeId} title={title} actions={dashboardActions}>
        {activeId === 'dashboard' ? (
          <DashboardSection rangeDays={rangeDays} />
        ) : activeId === 'users' ? (
          <UsersSection />
        ) : activeId === 'verifications' ? (
          <VerificationsSection />
        ) : activeId === 'roles' ? (
          <UserRolesSection />
        ) : activeId === 'suspended' ? (
          <SuspendedUsersSection />
        ) : activeId === 'banned' ? (
          <BannedUsersSection />
        ) : activeId === 'posts' ? (
          <PostsSection />
        ) : activeId === 'reports' ? (
          <ReportsSection />
        ) : activeId === 'moderation' ? (
          <ContentModerationSection />
        ) : activeId === 'hashtags' ? (
          <HashtagsSection />
        ) : activeId === 'media' ? (
          <MediaLibrarySection />
        ) : activeId === 'feedback' ? (
          <FeedbackSection />
        ) : activeId === 'council-teams' ? (
          <CouncilTeamsSection />
        ) : activeId === 'overview' ? (
          <OverviewSection />
        ) : activeId === 'user-statistics' ? (
          <UserStatisticsSection />
        ) : activeId === 'engagement' ? (
          <EngagementSection />
        ) : activeId === 'growth' ? (
          <GrowthSection />
        ) : activeId === 'retention' ? (
          <RetentionSection />
        ) : activeId === 'health' ? (
          <SystemHealthSection />
        ) : activeId === 'audit' ? (
          <AuditLogsSection />
        ) : activeId === 'settings' ? (
          <OwnerSettingsSection />
        ) : activeId === 'integrations' ? (
          <ApiIntegrationsSection />
        ) : activeId === 'backups' ? (
          <BackupsSection />
        ) : activeId === 'topic-refiner' ? (
          <TopicRefinerSection />
        ) : activeId === 'link-previews' ? (
          <LinkPreviewsSection />
        ) : activeId === 'appeals' ? (
          <AppealsSection />
        ) : (
          <PlaceholderSection label={title} />
        )}
      </OwnerLayout>
    </OwnerGuard>
  );
}
